import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCheck, Loader2, ArrowUpCircle, ArrowDownCircle, Equal } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}

export default function SettleOPADialog({ opa, onClose, onSettled }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [txnId, setTxnId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (opa) {
      setDate(new Date().toISOString().slice(0, 10));
      setTxnId(""); setRemarks(""); setConfirming(false);
    }
  }, [opa]);

  if (!opa) return null;

  const released = parseFloat(opa.amount || 0);
  const reimbursed = parseFloat((opa.reimbursement || {}).total_value || 0);
  const delta = +(reimbursed - released).toFixed(2);
  const direction = delta > 0 ? "pay" : delta < 0 ? "collect" : "even";
  const absDelta = Math.abs(delta);
  const txnRequired = direction !== "even";

  const handleClick = () => {
    if (!date) return toast.error("Settlement date is required");
    if (txnRequired && !txnId.trim()) return toast.error("Transaction ID is required when settling with a balance");
    if (!confirming) { setConfirming(true); return; }
    submit();
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/opa-requests/${opa.id}/settle`, {
        date,
        transaction_id: txnId.trim() || null,
        remarks: remarks.trim() || null,
      });
      toast.success(`Settled ${opa.code} · moved to Settled`);
      onSettled && onSettled(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Settlement failed");
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!opa} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-lg"
        data-testid="settle-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <CheckCheck className="w-5 h-5 text-cyan-400" /> Settle OPA
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Closing out <span className="font-mono text-emerald-400">{opa.code}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-md border border-white/10 bg-zinc-900 p-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">Payment Released</div>
            <div className="font-mono text-zinc-100 mt-0.5">{fmtINR(released)}</div>
          </div>
          <div className="rounded-md border border-white/10 bg-zinc-900 p-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500">Reimbursement Total</div>
            <div className="font-mono text-zinc-100 mt-0.5">{fmtINR(reimbursed)}</div>
          </div>
        </div>

        {direction === "even" ? (
          <div className="rounded-md border border-zinc-700 bg-zinc-900 p-3 flex items-center gap-3" data-testid="settle-even">
            <Equal className="w-5 h-5 text-zinc-400" />
            <div>
              <div className="text-sm text-zinc-200 font-medium">Nothing to collect or pay</div>
              <div className="text-xs text-zinc-500">Released matches reimbursement exactly. Transaction details optional.</div>
            </div>
          </div>
        ) : direction === "pay" ? (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-3" data-testid="settle-pay">
            <ArrowUpCircle className="w-5 h-5 text-emerald-400" />
            <div>
              <div className="text-sm text-emerald-300 font-medium">
                Pay Amount <span className="font-mono">{fmtINR(absDelta)}</span>
              </div>
              <div className="text-xs text-zinc-500">Reimbursement exceeds the advance — pay the balance to the requester.</div>
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-3" data-testid="settle-collect">
            <ArrowDownCircle className="w-5 h-5 text-amber-400" />
            <div>
              <div className="text-sm text-amber-300 font-medium">
                Collect Amount <span className="font-mono">{fmtINR(absDelta)}</span>
              </div>
              <div className="text-xs text-zinc-500">Reimbursement is less than the advance — collect the excess back from the requester.</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">
              Transaction ID / UTR {txnRequired && <span className="text-red-400">*</span>}
            </Label>
            <Input
              value={txnId}
              onChange={(e) => setTxnId(e.target.value)}
              placeholder="e.g., UTR2026062000123"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="settle-txn-id"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">Settlement Date <span className="text-red-400">*</span></Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1.5 bg-zinc-900 border-white/10 text-white"
              data-testid="settle-date"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">Remarks</Label>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Optional note about the settlement"
              className="mt-1.5 bg-zinc-900 border-white/10 text-white min-h-[70px]"
              data-testid="settle-remarks"
            />
          </div>
        </div>

        <DialogFooter className="mt-4 flex-col gap-3 sm:flex-col sm:items-stretch">
          {confirming && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2" data-testid="settle-confirm-banner">
              Are you sure? Click <span className="font-medium">Confirm Settlement</span> again to finalise this OPA and move it to <span className="font-medium">Settled</span>.
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => { if (confirming) setConfirming(false); else onClose(); }} disabled={saving} className="text-zinc-400 hover:text-white" data-testid="settle-cancel">{confirming ? "Go back" : "Cancel"}</Button>
            <Button
              onClick={handleClick}
              disabled={saving || !date || (txnRequired && !txnId.trim())}
              className={confirming ? "bg-amber-500 text-black hover:bg-amber-400 font-medium" : "bg-cyan-500 text-black hover:bg-cyan-400 font-medium"}
              data-testid="settle-submit"
            >
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              {confirming ? "Confirm Settlement" : "Settle OPA"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
